import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Camera } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { updateAgentDetails } from "../../../../services/agent_management_service.js";
import {
  getCoutries,
  getDistrictsBaseOnProvince,
  getProvincesBaseOnCountry,
} from "../../../../services/public_service.js";
import { useQuery } from "@tanstack/react-query";
import { getAgentById } from "../../../../services/agent_management_service.js";

// Function to detect if dark mode is active from Tailwind's dark class
const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const StyledContainer = styled("div")(({ theme }) => {
  const isDark = isDarkModeActive();
  return {
    maxWidth: "800px",
    margin: "0 auto",
    padding: theme.spacing(3),
    backgroundColor: isDark ? '#111827' : 'transparent',
    minHeight: '100vh',
  };
});

// Custom TextField with Tailwind dark mode support
const CustomTextField = ({ sx, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <TextField
      {...props}
      sx={{
        width: "100%",
        borderRadius: "20px",
        "& .MuiOutlinedInput-root": {
          paddingY: "0 !important",
          height: "46px",
          "& .MuiAutocomplete-input": { padding: "0 !important" },
          "&.Mui-focused fieldset": { borderColor: "#CD0C02 !important" },
          "&:hover fieldset": { borderColor: "#ff6666" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": { color: "#CD0C02 !important" },
        },
        "& .MuiInputBase-input": {
          color: isDark ? "#f3f4f6" : "#1f2937",
        },
        "& .MuiFormHelperText-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiAutocomplete-popupIndicator": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiAutocomplete-clearIndicator": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        ...sx,
      }}
    />
  );
};

// Custom Autocomplete with Tailwind dark mode support
const CustomAutocomplete = ({ options, getOptionLabel, value, onChange, renderInput, ...props }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={value}
      onChange={onChange}
      renderInput={renderInput}
      sx={{
        "& .MuiOutlinedInput-root": {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#ff6666",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#CD0C02 !important",
          },
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": {
            color: "#CD0C02 !important",
          },
        },
        "& .MuiAutocomplete-popupIndicator": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiAutocomplete-clearIndicator": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
      }}
      {...props}
    />
  );
};

const languages = [
  { label: "English", value: "english", icon: "https://flagcdn.com/us.svg" },
  { label: "Dari", value: "dari", icon: "https://flagcdn.com/af.svg" },
  { label: "Pashto", value: "pashto", icon: "https://flagcdn.com/af.svg" },
];

const statuses = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default function EditAgent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [payload, setPayload] = useState({
    parentAgentId: "",
    user_type: "agent",
    registrationType: "indirect",
    accountType: "retailer",
    alternativeContact: "",
    country: "",
    province: "",
    district: "",
    address: "",
    messageLanguage: "",
    commission_rate: 0,
    user: {
      username: "",
      email: "",
      mobileNumber: "",
      status: "",
      profilePicture: "",
    }
  });
  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState("");

  // Listen for dark mode changes
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        setLoading(true);
        const agent = await getAgentById(id);
        const IMG_BASE_URL = import.meta.env.VITE_IMG_BASE_URL;
        const profileUrl = agent.user?.profilePicture
          ? `${IMG_BASE_URL}${agent.user.profilePicture}`
          : "";
          
        setPayload({
          ...agent,
          country: agent.countryDetails?.id,
          province: agent.provinceDetails?.id,
          district: agent.districtDetails?.id,
          commission_rate: agent.commissionRateDetails?.percentage || 0,
          user: {
            username: agent.user?.username || "",
            email: agent.user?.email || "",
            mobileNumber: agent.user?.mobileNumber || "",
            status: agent.user?.status || "",
            profilePicture: profileUrl,
          }
        });
        setPreviewUrl(profileUrl);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching agent:", error);
        toast.error(t('editAgent.loadError') || "Failed to load agent data");
        setLoading(false);
      }
    };

    fetchAgent();
  }, [id, t]);

  const { data: countries, isLoading: loadingCountries } = useQuery({
    queryKey: ["countries"],
    queryFn: getCoutries,
  });

  const { data: provinces, isLoading: loadingProvinces } = useQuery({
    queryKey: ["provinces", payload?.country],
    queryFn: () => getProvincesBaseOnCountry(payload?.country),
    enabled: !!payload?.country,
  });

  const { data: districts, isLoading: loadingDistricts } = useQuery({
    queryKey: ["districts", payload?.province],
    queryFn: () => getDistrictsBaseOnProvince(payload?.province),
    enabled: !!payload?.province,
  });

  const validate = () => {
    const newErrors = {};
    if (!payload.user.username) newErrors.username = t('editAgent.errors.username') || "Agent name is required.";
    if (!payload.user.email) newErrors.email = t('editAgent.errors.email') || "Email is required.";
    if (!payload.user.mobileNumber)
      newErrors.mobileNumber = t('editAgent.errors.mobileNumber') || "Phone number is required.";
    if (!payload.country) newErrors.country = t('editAgent.errors.country') || "Country is required.";
    if (!payload.province) newErrors.province = t('editAgent.errors.province') || "Province is required.";
    if (!payload.district) newErrors.district = t('editAgent.errors.district') || "District is required.";
    if (!payload.address) newErrors.address = t('editAgent.errors.address') || "Address is required.";
    if (!payload.messageLanguage)
      newErrors.messageLanguage = t('editAgent.errors.messageLanguage') || "Language is required.";
    if (!payload.user.status) newErrors.status = t('editAgent.errors.status') || "Status is required.";
    
    if (payload.commission_rate === null || payload.commission_rate === undefined) {
      newErrors.commission_rate = t('editAgent.errors.commissionRate') || "Commission rate is required.";
    } else if (isNaN(payload.commission_rate)) {
      newErrors.commission_rate = t('editAgent.errors.commissionRateNumber') || "Must be a number";
    } else if (payload.commission_rate < 0) {
      newErrors.commission_rate = t('editAgent.errors.commissionRateNegative') || "Cannot be negative";
    } else if (payload.commission_rate > 100) {
      newErrors.commission_rate = t('editAgent.errors.commissionRateMax') || "Cannot exceed 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayloadChange = (field, value) => {
    if (field.startsWith('user.')) {
      const userField = field.split('.')[1];
      setPayload(prev => ({
        ...prev,
        user: {
          ...prev.user,
          [userField]: value
        }
      }));
    } else {
      setPayload(prev => ({ ...prev, [field]: value }));
    }
    setErrors(prev => ({ ...prev, [field.split('.')[1] || field]: null }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result);
        handlePayloadChange("user.profilePicture", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = async () => {
    if (!validate()) return;

    try {
      setUpdating(true);
      
      const updatePayload = {
        ...payload,
        commissionRateUid: payload.commissionRateUid,
        commission_rate: undefined
      };
      
      await updateAgentDetails(id, updatePayload);
      toast.success(t('editAgent.updateSuccess') || "Agent updated successfully");
      navigate("/manage-agent");
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.error || t('editAgent.updateError') || "Failed to update agent");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
        <CircularProgress sx={{ color: '#dc2626' }} />
      </div>
    );
  }

  return (
    <StyledContainer>
      <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
        {t('editAgent.title') || 'Edit Agent'}
      </h2>
      <div className="flex justify-center">
        <div className="relative w-[120px] h-[120px] rounded-full border border-red-500 dark:border-red-600 mb-4">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="profile"
              className="w-full h-full rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/default-avatar.png";
              }}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <Camera size={24} className="text-gray-500 dark:text-gray-400" />
            </div>
          )}
          <div
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-0 right-0 bg-white dark:bg-gray-800 rounded-full p-1 cursor-pointer shadow dark:shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <Camera size={20} className="text-gray-600 dark:text-gray-300" />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageUpload}
            />
          </div>
        </div>
      </div>
      <div className="w-full grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-6">
          <CustomTextField
            value={payload.user.username}
            onChange={(e) => handlePayloadChange("user.username", e.target.value)}
            variant="outlined"
            label={t('editAgent.agentName') || "Agent Name"}
            error={!!errors.username}
            helperText={errors.username}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomTextField
            value={payload.user.email}
            onChange={(e) => handlePayloadChange("user.email", e.target.value)}
            variant="outlined"
            label={t('editAgent.email') || "Email"}
            error={!!errors.email}
            helperText={errors.email}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomTextField
            value={payload.user.mobileNumber}
            onChange={(e) => handlePayloadChange("user.mobileNumber", e.target.value)}
            variant="outlined"
            label={t('editAgent.phoneNumber') || "Phone Number"}
            type="tel"
            error={!!errors.mobileNumber}
            helperText={errors.mobileNumber}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomTextField
            value={payload.alternativeContact}
            onChange={(e) => handlePayloadChange("alternativeContact", e.target.value)}
            variant="outlined"
            label={t('editAgent.alternativeContact') || "Alternative Contact"}
            error={!!errors.alternativeContact}
            helperText={errors.alternativeContact}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomAutocomplete
            options={countries?.data || []}
            getOptionLabel={(u) => u?.countryName}
            value={countries?.data?.find(c => c.id === payload.country) || null}
            onChange={(e, newValue) =>
              handlePayloadChange("country", newValue?.id)
            }
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t('editAgent.country') || "Country"}
                error={!!errors.country}
                helperText={errors.country}
                required
              />
            )}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomAutocomplete
            options={provinces?.data || []}
            getOptionLabel={(u) => u?.provinceName}
            value={provinces?.data?.find(p => p.id === payload.province) || null}
            onChange={(e, newValue) =>
              handlePayloadChange("province", newValue?.id)
            }
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t('editAgent.province') || "Province"}
                error={!!errors.province}
                helperText={errors.province}
                required
              />
            )}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomAutocomplete
            options={districts?.data || []}
            getOptionLabel={(u) => u?.districtName}
            value={districts?.data?.find(d => d.id === payload.district) || null}
            onChange={(e, newValue) =>
              handlePayloadChange("district", newValue?.id)
            }
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t('editAgent.district') || "District"}
                error={!!errors.district}
                helperText={errors.district}
                required
              />
            )}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomTextField
            value={payload.address}
            onChange={(e) => handlePayloadChange("address", e.target.value)}
            variant="outlined"
            label={t('editAgent.fullAddress') || "Full Address"}
            error={!!errors.address}
            helperText={errors.address}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomAutocomplete
            value={languages.find(l => l.value === payload.messageLanguage) || null}
            onChange={(e, newValue) => {
              if (newValue == null) {
                handlePayloadChange("messageLanguage", "");
              } else {
                handlePayloadChange("messageLanguage", newValue?.value);
              }
            }}
            options={languages}
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => (
              <Box
                component="li"
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1,
                  color: isDark ? '#f3f4f6' : '#1f2937',
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                }}
                {...props}
              >
                <img
                  src={option?.icon}
                  alt={option?.label}
                  className="w-5 h-5 rounded-full"
                />
                {option?.label}
              </Box>
            )}
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t('editAgent.language') || "Language"}
                placeholder={t('editAgent.selectLanguage') || "Select Language"}
                error={!!errors.messageLanguage}
                helperText={errors.messageLanguage}
              />
            )}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomTextField
            value={parseFloat(payload.commission_rate).toFixed(2)}
            onChange={(e) => handlePayloadChange("commission_rate", Number(e.target.value))}
            variant="outlined"
            label={t('editAgent.commissionRate') || "Commission Rate (%)"}
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            error={!!errors.commission_rate}
            helperText={errors.commission_rate}
          />
        </div>
        <div className="col-span-12 md:col-span-6">
          <CustomAutocomplete
            options={statuses || []}
            getOptionLabel={(u) => t(`status.${u?.value}`) || u?.label}
            value={statuses.find(s => s.value === payload.user.status) || null}
            onChange={(e, newValue) =>
              handlePayloadChange("user.status", newValue?.value)
            }
            renderInput={(params) => (
              <CustomTextField
                {...params}
                label={t('editAgent.status') || "Status"}
                error={!!errors.status}
                helperText={errors.status}
                required
              />
            )}
          />
        </div>
      </div>

      <div className="w-full flex justify-end mt-6 gap-3">
        <button
          onClick={() => navigate("/manage-agent")}
          className="py-2 px-6 border border-gray-400 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          {t('common.cancel') || "Cancel"}
        </button>
        <button
          onClick={handleUpdate}
          className="text-white bg-[#CD0C02] hover:bg-[#b00b02] dark:bg-red-700 dark:hover:bg-red-800 py-2 px-6 rounded-md disabled:opacity-50 transition-colors"
          disabled={updating}
        >
          {updating ? <CircularProgress size={20} color="inherit" /> : (t('common.update') || "Update Agent")}
        </button>
      </div>
    </StyledContainer>
  );
}